import React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { bool, func, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Switch } from 'antd';
import waitForStoreUpdate from '@utils/waitForStoreUpdate';
import confirmCareTeamAssignmentImpact from '@utils/confirmCareTeamAssignmentImpact';
import { Card } from '@cards/Card';
import DetailForm from '@components/DetailForm';
import DetailRow from '@components/DetailRow';
import { toggleDashboardUserActivation } from '@redux/dashboardUsers/actions';
import { selectDashboardUserActivating } from '@redux/dashboardUsers/reducers';
import './style.less';

function UserAccessCard({
  activating,
  dashboardUserId,
  hasAccess,
  hospitalId,
  loading,
  resending,
  resendInvite,
  userName,
  userEmail,
  userUUID,
}) {
  const dispatch = useDispatch();
  const store = useStore();
  const { t } = useTranslation();

  const runActivation = () => {
    dispatch(toggleDashboardUserActivation(dashboardUserId, hasAccess, userName, hospitalId));
    return waitForStoreUpdate(store, state => [selectDashboardUserActivating(state)]);
  };

  const onToggleAccess = () => {
    if (!hasAccess) {
      Modal.confirm({
        title: t('cards:DashboardUsersAccess.modals.activate.title', { name: userName }),
        okText: t('cards:DashboardUsersAccess.modals.activate.ok'),
        onOk: runActivation,
      });
      return;
    }

    confirmCareTeamAssignmentImpact({
      organisationSlug: hospitalId,
      userId: dashboardUserId,
      dispatch,
      store,
      t,
      whenEmpty: 'simple-confirm',
      simpleConfirm: {
        title: t('cards:DashboardUsersAccess.modals.deactivate.title', { name: userName }),
        okText: t('cards:DashboardUsersAccess.modals.deactivate.ok'),
        okType: 'danger',
      },
      onConfirm: runActivation,
    });
  };

  return (
    <Card
      className="dashboard-users-user-access"
      loading={loading}
      title={t('cards:DashboardUsersAccess.title')}
    >
      <DetailForm>
        <DetailRow
          label={t('cards:DashboardUsersAccess.labels.activate')}
          value={<Switch checked={hasAccess} loading={activating} onClick={onToggleAccess} />}
        />
      </DetailForm>
      {userEmail === userUUID ? (
        <Button disabled={resending} type="primary" onClick={resendInvite}>
          {t('cards:DashboardUsersAccess.resendCode')}
        </Button>
      ) : null}
    </Card>
  );
}

UserAccessCard.propTypes = {
  activating: bool,
  dashboardUserId: string,
  hasAccess: bool,
  hospitalId: string,
  loading: bool.isRequired,
  resending: bool,
  resendInvite: func.isRequired,
  userName: string,
  userEmail: string,
  userUUID: string,
};

UserAccessCard.defaultProps = {
  activating: false,
  resending: false,
  hasAccess: undefined,
  hospitalId: undefined,
  userName: undefined,
  userEmail: undefined,
  userUUID: undefined,
  dashboardUserId: undefined,
};

export default UserAccessCard;
